// Per-clip audio operations used by the Clip Inspector.
// Gain/normalize/fades are non-destructive (clip params). Reverse / clean-voice /
// reduce-noise render a NEW asset and repoint the clip (undoable; old asset kept).
import { Store } from "../state/store";
import { AudioEngine } from "../audio/engine";
import { Clip } from "../state/model";
import { denoiseChannel } from "./noise-reduction";

function regionFrames(buf: AudioBuffer, clip: Clip) {
  const sr = buf.sampleRate;
  const start = Math.max(0, Math.floor(clip.offset * sr));
  const len = Math.min(buf.length - start, Math.floor(clip.duration * sr));
  return { sr, start, len: Math.max(1, len) };
}

/** Linear peak of the clip's audible region. */
export function clipPeak(store: Store, clip: Clip): number {
  const buf = store.buffers.get(clip.assetId);
  if (!buf) return 0;
  const { start, len } = regionFrames(buf, clip);
  let peak = 0;
  for (let ch = 0; ch < buf.numberOfChannels; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = start; i < start + len; i++) { const a = Math.abs(d[i]); if (a > peak) peak = a; }
  }
  return peak;
}

export const linToDb = (v: number) => (v <= 1e-6 ? -Infinity : 20 * Math.log10(v));

/** Set clip gain so its peak hits targetDb (default -1 dBFS). Non-destructive. */
export function normalizeClip(store: Store, clip: Clip, targetDb = -1): boolean {
  const peak = clipPeak(store, clip);
  if (peak <= 0) return false;
  const target = Math.pow(10, targetDb / 20);
  store.updateClip(clip.id, { gain: Math.min(16, target / peak) });
  return true;
}

/** Copy the clip's region into a fresh buffer (for destructive edits). */
function copyRegion(engine: AudioEngine, buf: AudioBuffer, clip: Clip): AudioBuffer {
  const { sr, start, len } = regionFrames(buf, clip);
  const out = engine.ctx.createBuffer(buf.numberOfChannels, len, sr);
  for (let ch = 0; ch < buf.numberOfChannels; ch++) {
    out.getChannelData(ch).set(buf.getChannelData(ch).subarray(start, start + len));
  }
  return out;
}

/** Render the clip's region through a node chain into a new buffer. */
async function renderRegion(engine: AudioEngine, buf: AudioBuffer, clip: Clip, build: (ctx: OfflineAudioContext) => { input: AudioNode; output: AudioNode }): Promise<AudioBuffer> {
  const { sr, len } = regionFrames(buf, clip);
  const ctx = new OfflineAudioContext(buf.numberOfChannels, len, sr);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const { input, output } = build(ctx);
  src.connect(input); output.connect(ctx.destination);
  src.start(0, clip.offset, clip.duration);
  return ctx.startRendering();
}

function commit(store: Store, clip: Clip, newBuf: AudioBuffer, suffix: string) {
  const asset = store.addAsset(newBuf, `${clip.name} ${suffix}`);
  store.pushUndo();
  store.updateClip(clip.id, { assetId: asset.id, offset: 0, duration: newBuf.duration }, false);
}

/** Estimate the steady noise floor (10th-percentile short-window RMS). */
function noiseFloor(buf: AudioBuffer): number {
  const ch = buf.getChannelData(0);
  const win = Math.max(1, Math.floor(buf.sampleRate * 0.02));
  const rms: number[] = [];
  for (let i = 0; i + win <= ch.length; i += win) {
    let s = 0; for (let j = 0; j < win; j++) s += ch[i + j] * ch[i + j];
    rms.push(Math.sqrt(s / win));
  }
  if (!rms.length) return 0;
  rms.sort((a, b) => a - b);
  return rms[Math.floor(rms.length * 0.1)] || 0;
}

/** In-place downward gate/expander: attenuate audio below an envelope threshold. */
function applyGate(buf: AudioBuffer, threshold: number, reduction = 0.12) {
  const sr = buf.sampleRate, n = buf.length;
  const chans: Float32Array[] = [];
  for (let c = 0; c < buf.numberOfChannels; c++) chans.push(buf.getChannelData(c));
  const aA = Math.exp(-1 / (0.005 * sr)), aR = Math.exp(-1 / (0.08 * sr));
  const gains = new Float32Array(n);
  let env = 0, g = 1;
  for (let i = 0; i < n; i++) {
    let x = 0; for (const d of chans) { const a = Math.abs(d[i]); if (a > x) x = a; }
    env = (x > env ? aA : aR) * env + (1 - (x > env ? aA : aR)) * x;
    const target = env >= threshold ? 1 : reduction;
    g = (target < g ? aA : aR) * g + (1 - (target < g ? aA : aR)) * target;
    gains[i] = g;
  }
  for (const d of chans) for (let i = 0; i < n; i++) d[i] *= gains[i];
}

export async function reverseClip(store: Store, engine: AudioEngine, clip: Clip) {
  const buf = store.buffers.get(clip.assetId); if (!buf) return;
  const out = copyRegion(engine, buf, clip);
  for (let ch = 0; ch < out.numberOfChannels; ch++) out.getChannelData(ch).reverse();
  commit(store, clip, out, "(rev)");
}

export async function cleanVoiceClip(store: Store, engine: AudioEngine, clip: Clip) {
  const buf = store.buffers.get(clip.assetId); if (!buf) return;
  const out = await renderRegion(engine, buf, clip, (ctx) => {
    const hp = ctx.createBiquadFilter(); hp.type = "highpass"; hp.frequency.value = 85;
    const presence = ctx.createBiquadFilter(); presence.type = "highshelf"; presence.frequency.value = 4500; presence.gain.value = 2.5;
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -26; comp.ratio.value = 3; comp.attack.value = 0.003; comp.release.value = 0.2;
    hp.connect(presence); presence.connect(comp);
    return { input: hp, output: comp };
  });
  applyGate(out, Math.max(noiseFloor(out) * 3, 0.004));
  commit(store, clip, out, "(voice)");
}

export async function reduceNoiseClip(store: Store, engine: AudioEngine, clip: Clip) {
  const buf = store.buffers.get(clip.assetId); if (!buf) return;
  const out = copyRegion(engine, buf, clip);
  applyGate(out, Math.max(noiseFloor(out) * 2.5, 0.003), 0.18);
  commit(store, clip, out, "(nr)");
}

/** Downmix the clip's region to mono and return the quietest `seconds`-long
 *  window — a good candidate for sampling a noise profile from the recording. */
export function clipQuietestMono(store: Store, clip: Clip, seconds = 0.8): Float32Array | null {
  const buf = store.buffers.get(clip.assetId); if (!buf) return null;
  const { sr, start, len } = regionFrames(buf, clip);
  const mono = new Float32Array(len);
  for (let ch = 0; ch < buf.numberOfChannels; ch++) { const d = buf.getChannelData(ch); for (let i = 0; i < len; i++) mono[i] += d[start + i]; }
  if (buf.numberOfChannels > 1) for (let i = 0; i < len; i++) mono[i] /= buf.numberOfChannels;
  const win = Math.min(len, Math.floor(sr * seconds));
  if (win < sr * 0.1) return mono; // too short to scan — use the whole thing
  let best = Infinity, bestAt = 0;
  const step = Math.max(1, Math.floor(win / 4));
  for (let s = 0; s + win <= len; s += step) {
    let e = 0; for (let i = 0; i < win; i++) { const v = mono[s + i]; e += v * v; }
    if (e < best) { best = e; bestAt = s; }
  }
  return mono.subarray(bestAt, bestAt + win);
}

/** Spectral noise reduction using a previously sampled noise profile. */
export async function reduceNoiseProfileClip(store: Store, engine: AudioEngine, clip: Clip, profile: Float32Array, strength = 0.7): Promise<boolean> {
  const buf = store.buffers.get(clip.assetId); if (!buf) return false;
  const out = copyRegion(engine, buf, clip);
  for (let ch = 0; ch < out.numberOfChannels; ch++) out.getChannelData(ch).set(denoiseChannel(out.getChannelData(ch), profile, strength, 0.5, out.sampleRate));
  commit(store, clip, out, "(nr)");
  return true;
}

/** Downmix the clip's region to mono. Returns false if already mono. */
export async function makeMonoClip(store: Store, engine: AudioEngine, clip: Clip): Promise<boolean> {
  const buf = store.buffers.get(clip.assetId); if (!buf) return false;
  if (buf.numberOfChannels === 1) return false;
  const { sr, start, len } = regionFrames(buf, clip);
  const out = engine.ctx.createBuffer(1, len, sr);
  const o = out.getChannelData(0);
  const chans: Float32Array[] = [];
  for (let c = 0; c < buf.numberOfChannels; c++) chans.push(buf.getChannelData(c));
  for (let i = 0; i < len; i++) {
    let s = 0; for (const d of chans) s += d[start + i];
    o[i] = s / buf.numberOfChannels;
  }
  commit(store, clip, out, "(mono)");
  return true;
}

/** Trim leading/trailing near-silence by adjusting offset/duration (non-destructive). */
export function trimSilenceClip(store: Store, clip: Clip): boolean {
  const buf = store.buffers.get(clip.assetId); if (!buf) return false;
  const { sr, start, len } = regionFrames(buf, clip);
  const thr = Math.max(clipPeak(store, clip) * 0.02, 0.003);
  const chans: Float32Array[] = [];
  for (let c = 0; c < buf.numberOfChannels; c++) chans.push(buf.getChannelData(c));
  const peakAt = (i: number) => { let p = 0; for (const d of chans) { const a = Math.abs(d[start + i]); if (a > p) p = a; } return p; };
  let head = 0; while (head < len && peakAt(head) < thr) head++;
  let tail = 0; while (tail < len - head && peakAt(len - 1 - tail) < thr) tail++;
  if (head === 0 && tail === 0) return false;
  if (head >= len - tail) return false; // entirely silent
  store.updateClip(clip.id, { offset: clip.offset + head / sr, duration: (len - head - tail) / sr });
  return true;
}

/** Pitch-preserving time-stretch (overlap-add) to a target scale (>1 = longer). */
function olaStretch(engine: AudioEngine, buf: AudioBuffer, timeScale: number): AudioBuffer {
  const sr = buf.sampleRate, frame = 2048, hop = 512;
  const synHop = Math.max(1, Math.round(hop * timeScale));
  const outLen = Math.ceil(buf.length * timeScale) + frame;
  const out = engine.ctx.createBuffer(buf.numberOfChannels, outLen, sr);
  const win = new Float32Array(frame);
  for (let i = 0; i < frame; i++) win[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (frame - 1));
  for (let ch = 0; ch < buf.numberOfChannels; ch++) {
    const inD = buf.getChannelData(ch), outD = out.getChannelData(ch);
    const norm = new Float32Array(outLen);
    let inPos = 0, outPos = 0;
    while (inPos + frame < buf.length && outPos + frame < outLen) {
      for (let i = 0; i < frame; i++) { outD[outPos + i] += inD[inPos + i] * win[i]; norm[outPos + i] += win[i]; }
      inPos += hop; outPos += synHop;
    }
    for (let i = 0; i < outLen; i++) if (norm[i] > 1e-6) outD[i] /= norm[i];
  }
  return out;
}

/** Detect the clip's BPM and time-stretch it to the project tempo (pitch kept). */
export async function stretchToTempoClip(store: Store, engine: AudioEngine, clip: Clip): Promise<number | null> {
  const bpm = analyzeTempo(store, clip);
  if (!bpm) return null;
  const timeScale = bpm / store.project.tempo;
  if (Math.abs(timeScale - 1) < 0.01) return bpm; // already on tempo
  const buf = store.buffers.get(clip.assetId); if (!buf) return null;
  const region = copyRegion(engine, buf, clip);
  commit(store, clip, olaStretch(engine, region, timeScale), `(${store.project.tempo}bpm)`);
  return bpm;
}

/** Rough tempo estimate (BPM) from the clip's onset envelope via autocorrelation. */
export function analyzeTempo(store: Store, clip: Clip): number | null {
  const buf = store.buffers.get(clip.assetId); if (!buf) return null;
  const { sr, start, len } = regionFrames(buf, clip);
  const ch = buf.getChannelData(0);
  const hop = 512;
  const env: number[] = [];
  for (let i = start; i + hop <= start + len; i += hop) {
    let s = 0; for (let j = 0; j < hop; j++) s += ch[i + j] * ch[i + j];
    env.push(Math.sqrt(s / hop));
  }
  if (env.length < 16) return null;
  const onset = env.map((v, i) => (i ? Math.max(0, v - env[i - 1]) : 0));
  const mean = onset.reduce((a, b) => a + b, 0) / onset.length;
  for (let i = 0; i < onset.length; i++) onset[i] -= mean;
  const fps = sr / hop;
  const minLag = Math.floor((fps * 60) / 180), maxLag = Math.ceil((fps * 60) / 60);
  let best = 0, bestLag = 0;
  for (let lag = minLag; lag <= maxLag; lag++) {
    let s = 0; for (let i = 0; i + lag < onset.length; i++) s += onset[i] * onset[i + lag];
    if (s > best) { best = s; bestLag = lag; }
  }
  if (!bestLag) return null;
  let bpm = (60 * fps) / bestLag;
  while (bpm < 70) bpm *= 2;
  while (bpm > 160) bpm /= 2;
  return Math.round(bpm);
}
